import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Stack,
  InputAdornment,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AddPhotoAlternate, CurrencyRupee } from "@mui/icons-material";
import FormikTextField from "../../../components/form/FormikTextField";
import PageHeader from "../../../components/page-layout/PageHeader";

const validationSchema = Yup.object({
  name: Yup.string().required("Store name is required"),
  openTime: Yup.string().required("Open time is required"),
  closeTime: Yup.string().required("Close time is required"),
  latitude: Yup.number().required("Latitude is required"),
  longitude: Yup.number().required("Longitude is required"),
  image: Yup.mixed().required("Banner image is required"),
  platformFee: Yup.number()
    .min(0, "Platform fee must be 0 or greater")
    .required("Platform fee is required"),
});

const StoreStep = ({ formData = {}, setFormData, onBack, nextStep }) => {
  const [imagePreview, setImagePreview] = React.useState(null);

  const [initialValues, setInitalValues] = useState({
    name: "",
    openTime: "",
    closeTime: "",
    latitude: "",
    longitude: "",
    image: null,
    status: "ACTIVE",
    platformFee: 0,
  });

  useEffect(() => {
    if (formData.store) {
      setInitalValues(formData.store);
      if (formData.store.image && typeof formData.store.image === "object") {
        setImagePreview(URL.createObjectURL(formData.store.image));
      }
    }
  }, [formData.store]);

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    setFieldValue("image", file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          // handle submit
          console.log(values);
          setFormData((prev) => ({ ...prev, store: values }));
          nextStep();
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          dirty,
          isValid,
          handleSubmit,
        }) => (
          <>
            <Box>
              <PageHeader
                title={"Store Details"}
                lead={
                  "Please fill in the store details and upload a banner image to continue."
                }
              />

              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        borderWidth: 1,
                        borderStyle: values.image ? "solid" : "dashed",
                        borderColor: grey[300],
                        borderRadius: 2,
                        p: 1,
                        aspectRatio: "16/9",
                      }}
                    >
                      {values.image ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems={"flex-end"}
                          position={"relative"}
                          height={"100%"}
                        >
                          <img
                            src={imagePreview}
                            alt="Preview"
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                          >
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              name="image"
                              id="image-upload"
                              onChange={(e) =>
                                handleImageChange(e, setFieldValue)
                              }
                            />
                            Re-Upload Banner
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <AddPhotoAlternate
                            sx={{
                              fontSize: "48px",
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                          >
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              name="image"
                              id="image-upload"
                              onChange={(e) =>
                                handleImageChange(e, setFieldValue)
                              }
                            />
                            Upload Banner
                          </Button>
                          <Typography
                            variant="body2"
                            sx={{ textAlign: "center" }}
                          >
                            Click to upload banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack gap={2}>
                      <FormikTextField label="Store Name" name="name" />
                      <Stack flexDirection={"row"} gap={2}>
                        <FormikTextField
                          label="Open Time"
                          name="openTime"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                        />
                        <FormikTextField
                          label="Close Time"
                          name="closeTime"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>

                      <Stack flexDirection={"row"} gap={2}>
                        <FormikTextField label="Latitude" name="latitude" />
                        <FormikTextField label="Longitude" name="longitude" />
                      </Stack>
                      <Stack flexDirection={"row"} gap={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.status === "ACTIVE"}
                              onChange={(e) =>
                                setFieldValue(
                                  "status",
                                  e.target.checked ? "ACTIVE" : "INACTIVE"
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Typography
                              fontWeight={600}
                              color={
                                values.status === "ACTIVE"
                                  ? "primary"
                                  : "text.secondary"
                              }
                            >
                              {values.status === "ACTIVE"
                                ? "Active"
                                : "Inactive"}
                            </Typography>
                          }
                          sx={{ mt: 2 }}
                        />
                        <FormikTextField
                          label="Platform Fee"
                          name="platformFee"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CurrencyRupee fontSize="16px" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    {/* <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 700,
              fontSize: "1.1rem",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            Save Store
          </Button> */}
                  </Grid>
                </Grid>
              </Form>
            </Box>
            <Stack
              gap={2}
              mt={2}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={!onBack}
                onClick={onBack}
              >
                Back
              </Button>

              <Button
                type="submit"
                variant="contained"
                // disabled={!formData.vendor}
                disabled={!isValid}
                onClick={() => {
                  handleSubmit();
                }}
              >
                Save & Next
              </Button>
            </Stack>
          </>
        )}
      </Formik>
    </Box>
  );
};

export default StoreStep;
